import { FC, useEffect, useState } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import type { PopulationSegment } from "@models/populationSegment.model";
import type { JourneyPath } from "@models/journeyPath.model";

interface Props {
  show: boolean;
  mode: "add" | "rename";
  initialName?: string;
  initialSegmentId?: number | null;
  /** The org's populations for the group's scope picker. */
  segments?: readonly PopulationSegment[];
  /**
      Populations a live group already prices, segment id → that group's name.
      One group per population, contract-wide (MPN-1592): the server refuses a
      second one, so these stay on the menu but greyed out, naming the group.
  */
  takenSegments?: ReadonlyMap<number, string>;
  onClose: () => void;
  onSubmit: (name: string, populationSegmentId: number) => void;
}

/** Build `takenSegments`: every group on the contract's OTHER sheets (server
 *  truth) plus this sheet's drafts (which may be unsaved), minus the group being
 *  edited — its own population is not taken from itself. */
export function takenPopulations(
  contractGroups: readonly JourneyPath[],
  sheetGroups: readonly { id: number; name: string; populationSegmentId: number | null }[],
  currentSheetId: number | null | undefined,
  editingGroupId: number | undefined,
): Map<number, string> {
  const taken = new Map<number, string>();
  for (const g of contractGroups) {
    if (g.price_sheet_id !== currentSheetId && g.population_segment_id !== null) {
      taken.set(g.population_segment_id, g.name);
    }
  }
  for (const p of sheetGroups) {
    if (p.id !== editingGroupId && p.populationSegmentId !== null) {
      taken.set(p.populationSegmentId, p.name);
    }
  }
  return taken;
}

const NO_TAKEN: ReadonlyMap<number, string> = new Map();

/*
    A pricing group prices ONE population. The population is the primary field
    and the name follows from it — every group on prod is named after its list.
*/
const JourneyPathNameModal: FC<Props> = ({
  show,
  mode,
  initialName = "",
  initialSegmentId = null,
  segments = [],
  takenSegments = NO_TAKEN,
  onClose,
  onSubmit,
}) => {
  const [name, setName] = useState(initialName);
  const [scope, setScope] = useState<string>("");
  const [nameTouched, setNameTouched] = useState(false);

  useEffect(() => {
    if (show) {
      setName(initialName);
      setScope(initialSegmentId !== null ? String(initialSegmentId) : "");
      setNameTouched(!!initialName);
    }
  }, [show, initialName, initialSegmentId]);

  const trimmed = name.trim();
  const scopeChosen = scope !== "";

  const handleScopeChange = (value: string) => {
    setScope(value);
    if (nameTouched) return;
    const segment = segments.find((s) => String(s.id) === value);
    setName(segment?.name ?? "");
  };

  const handleSubmit = () => {
    if (!trimmed || !scopeChosen) return;
    onSubmit(trimmed, Number(scope));
  };

  const title = mode === "add" ? "Add Group" : "Edit Group";
  const submitLabel = mode === "add" ? "Add Group" : "Save";

  return (
    <Modal show={show} onHide={onClose} centered className="tier-2-modal" backdropClassName="tier-2-backdrop">
      <Modal.Header closeButton>
        <Modal.Title as="h5">{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit();
          }}
        >
          <Form.Group>
            <Form.Label className="fw-semibold">Population</Form.Label>
            <Form.Select autoFocus value={scope} onChange={(e) => handleScopeChange(e.target.value)}>
              <option value="">Choose the population this group prices</option>
              {segments.map((s) => {
                const takenBy = takenSegments.get(s.id);
                return (
                  <option key={s.id} value={s.id} disabled={takenBy !== undefined}>
                    {s.name}
                    {takenBy !== undefined ? ` — priced by "${takenBy}"` : ""}
                  </option>
                );
              })}
            </Form.Select>
            <Form.Text className="text-muted">
              Campaigns on this population bill from this group's steps. Each population
              gets exactly one group on the contract.
            </Form.Text>
          </Form.Group>
          <Form.Group className="mt-3">
            <Form.Label className="fw-semibold">Name</Form.Label>
            <Form.Control
              type="text"
              value={name}
              placeholder="Defaults to the population's name"
              onChange={(e) => {
                setNameTouched(true);
                setName(e.target.value);
              }}
            />
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="light" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSubmit} disabled={!trimmed || !scopeChosen}>
          <i className="bi bi-check-circle me-2" />
          {submitLabel}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default JourneyPathNameModal;
